import { useEffect, useState } from "react";
import { Loader2, Check, Camera, Trash2, Settings as SettingsIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export default function CreatorProfile() {
  const { setUser } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", phone: "", bio: "" });
  const [avatar, setAvatar] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [aBusy, setABusy] = useState(false);

  useEffect(() => {
    api.get("/vid/creator/me").then((r) => {
      setMe(r.data);
      const c = r.data.creator;
      setForm({ name: c.name || "", username: c.slug || "", phone: c.phone || "", bio: c.bio || "" });
      setAvatar(c.avatar || "");
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!me) return <LoadingRows rows={4} />;

  const refreshAuth = async () => { const { data } = await api.get("/auth/me"); setUser(data); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.patch("/vid/creator/profile", form);
      toast.success("Profile updated");
      await refreshAuth();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Only JPG, PNG or WebP images"); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Image must be under 3 MB"); return; }
    const rd = new FileReader();
    rd.onload = () => setPreview(rd.result);
    rd.readAsDataURL(f);
  };

  const uploadAvatar = async () => {
    setABusy(true);
    try {
      const { data } = await api.post("/vid/creator/avatar", { image: preview });
      setAvatar(data.avatar); setPreview("");
      setMe({ ...me, creator: { ...me.creator, avatar: data.avatar } });
      toast.success("Profile picture updated everywhere");
      await refreshAuth();
    } catch (e) { toast.error(errMsg(e)); } finally { setABusy(false); }
  };

  const removeAvatar = async () => {
    setABusy(true);
    try {
      await api.patch("/vid/creator/profile", { clear_avatar: true });
      setAvatar(""); setPreview("");
      setMe({ ...me, creator: { ...me.creator, avatar: "" } });
      toast.success("Profile picture removed");
      await refreshAuth();
    } catch (e) { toast.error(errMsg(e)); } finally { setABusy(false); }
  };

  const shown = preview || avatar;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="creator-profile-page">
      <PageHeader title="Edit Channel Profile" subtitle="How viewers see you on MILTROO Videos" testid="profile-title" />

      <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-20 w-20 rounded-full overflow-hidden gradient-bg text-white flex items-center justify-center font-display font-extrabold text-2xl shrink-0" data-testid="profile-avatar">
            {shown
              ? <img src={shown} alt={form.name} className="h-full w-full object-cover" />
              : (form.name || "C")[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold truncate">{me.creator.name}</p>
            <p className="text-xs text-muted-foreground truncate">{me.creator.email} · Free Creator account</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold cursor-pointer text-white gradient-bg" data-testid="avatar-pick-label">
                <Camera className="h-3.5 w-3.5" /> {avatar || preview ? "Change Photo" : "Upload Photo"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage} data-testid="avatar-pick" />
              </label>
              {avatar && !preview && (
                <button onClick={removeAvatar} disabled={aBusy} data-testid="avatar-remove"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold border border-red-500/40 text-red-500">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {preview && (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="avatar-preview-bar">
            <p className="text-xs font-semibold flex-1">Preview — save to apply this photo everywhere</p>
            <Button size="sm" onClick={uploadAvatar} disabled={aBusy} data-testid="avatar-save" className="rounded-full font-bold gradient-bg text-white border-0">
              {aBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Photo"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreview("")} data-testid="avatar-cancel" className="rounded-full font-bold">Cancel</Button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Channel Name *</Label>
            <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input data-testid="profile-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="h-11 rounded-xl" placeholder="telugu-tech-creator" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Phone</Label>
          <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl" placeholder="+91…" />
        </div>
        <div className="space-y-1.5">
          <Label>Channel Description</Label>
          <Textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Tell viewers what your videos are about…" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button data-testid="profile-save" onClick={save} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0 px-8">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" />Save Profile</>}
          </Button>
          <Link to="/creator/settings" data-testid="goto-account-settings"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-sm font-bold border border-border hover:bg-muted transition-colors">
            <SettingsIcon className="h-4 w-4" /> Account Settings
          </Link>
          <Link to={`/channel/${me.creator.slug}`} data-testid="goto-public-channel"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-sm font-bold text-primary hover:underline">
            View public channel →
          </Link>
        </div>
      </div>
    </div>
  );
}
